import { Drawer, Group, Skeleton, Stack, Text, Button, Paper } from "@mantine/core";

import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { Account } from "./AccountCard";
import SyncRuleRow from "./SyncRuleRow";
import SyncRuleModal from "./SyncRuleModal";
import { useEffect, useState } from "react";
import API from "../utils/const";
import { MessageKind, withLoading } from "../utils/common";
import { createToast } from "./Toast";
import { SyncRule } from "../utils/rule";
import { useAuth } from "../contexts/AuthContext";
import { authFetch } from "../utils/session";
import { IconArrowsRightLeft, IconUserPlus } from "@tabler/icons-react";

export const SynchronizationsTab = ({
    accounts,
    accountsLoaded,
    openTab,
}: {
    accounts: Account[];
    accountsLoaded: boolean;
    openTab: (tab: string) => void;
}) => {
    const { t } = useTranslation(['app']);
    const [openedDraft, { open: openDraft, close: closeDraft }] = useDisclosure(false);
    const [rules, setRules] = useState<SyncRule[]>([])
    const { isAuthenticated } = useAuth()
    const [loading, setLoading] = useState(true)


    const fetchSyncRule = async () => {
        const response = await authFetch(
            `${API}/sync`,
            {
                method: 'GET'
            }
        )
        if (!response.ok) {
            createToast(t("dashboard.error.loading-rules"), MessageKind.Error)
            return
        }
        const rules_ = await response.json();
        setRules(rules_);
    }

    async function successNewRuleToast() {
        createToast("🎊 Rule created! Events should start appearing as we speak, please wait a few minutes", MessageKind.Info)
    }

    useEffect(() => {
        if (isAuthenticated) {
            withLoading(setLoading)(fetchSyncRule)();
        }
        else {
            setLoading(false)
        }
    }, [isAuthenticated])

    const hasAccountsButNoRules = accountsLoaded && accounts.length > 0 && !loading && rules.length === 0

    if (!accountsLoaded) {
        return (
            <Stack gap="sm" my="md">
                <Skeleton height={36} width="60%" radius="sm" />
                <Skeleton height={72} radius="md" />
                <Skeleton height={72} radius="md" />
            </Stack>
        );
    }

    if (!accounts || accounts.length === 0) {
        return (
            <Paper shadow="xs" p="md" radius="md" bg='gray.1' withBorder my="md">
                <Stack gap="md">
                    <Text>{t("dashboard.no-accounts-for-sync")}</Text>
                    <Button leftSection={<IconUserPlus stroke={1} />} color='primary.4' variant="outline" onClick={() => openTab("accounts")}>
                        <Text span fw={300}>{t("dashboard.sync.connect-another-account")}</Text>
                    </Button>
                </Stack>
            </Paper>
        );
    }

    return (
        <>
            <div className='d-md-flex align-items-center justify-content-center d-flex-row my-3 px-0'>
                <span className='display-5 me-auto mb-2 mb-sm-0'>{t("dashboard.synchronize-calendars")}</span>
                {!hasAccountsButNoRules &&
                    <Button leftSection={<IconArrowsRightLeft stroke={0.7} />} color='primary.4' onClick={openDraft}><Text span fw={300}>{t("dashboard.sync.add-synchronization")}</Text></Button>
                }
            </div>

            {loading && (
                <Stack gap="sm" my="md">
                    <Skeleton height={72} radius="md" />
                    <Skeleton height={72} radius="md" />
                </Stack>
            )}
            {hasAccountsButNoRules &&
                <Paper shadow="xs" p="md" radius="md" bg='gray.1' withBorder>
                    <Stack gap="md">
                        <Text>{t("dashboard.no-syncs")}</Text>
                        <Group>
                            <Button leftSection={<IconArrowsRightLeft stroke={0.7} />} color='primary.4' onClick={openDraft}><Text span fw={300}>{t("dashboard.sync.add-synchronization")}</Text></Button>
                            <Button leftSection={<IconUserPlus stroke={1} />} color='primary.4' variant="outline" onClick={() => openTab("accounts")}><Text span fw={300}>{t("dashboard.sync.connect-another-account")}</Text></Button>
                        </Group>
                    </Stack>
                </Paper>
            }
            <Stack gap={'8px'}>
                {rules.length > 0 && rules.map((rule) => <SyncRuleRow accounts={accounts} key={rule.uuid} rule={rule} onChange={() => withLoading(setLoading)(fetchSyncRule)()} />)
                }
            </Stack>
            <Drawer
                opened={openedDraft} onClose={closeDraft}
                withCloseButton={true}
                transitionProps={{ duration: 500 }}
                size='xl'
            >
                <SyncRuleModal close={closeDraft} rule={null} accounts={accounts} successCallback={() => { withLoading(setLoading)(fetchSyncRule)(); closeDraft(); successNewRuleToast(); }} />
            </Drawer>
        </>
    )
}